import { useEffect, useRef } from 'react';
import { HeatmapColorOptions, MIN_STEP_FALLBACK_MS } from '@/core';
import { ColorScales, CustomColorScale } from './color-scales';
import { HeatMapChartData } from './heat-map-model';

export type HeatmapCanvasColorOptions = HeatmapColorOptions & {
  customColorScale?: CustomColorScale;
};

// render a pixel in the given color to an index in the bitmap array
function writePixel(data: Uint8ClampedArray, idx: number, color: [number, number, number]) {
  data[idx] = color[0]; // red
  data[idx + 1] = color[1]; // green
  data[idx + 2] = color[2]; // blue
  data[idx + 3] = 255; // alpha
}

// find the color corresponding to a given value
export function colorLookup(value: number, maxValue: number, colorOptions: HeatmapCanvasColorOptions) {
  if (colorOptions.customColorScale) {
    return colorOptions.customColorScale.getColor(value, { maxValue, colorOptions });
  }
  const min = Math.max(0, colorOptions.min ?? 0); // min value must be >=0
  const max = colorOptions.max ?? maxValue;
  const colorScale = ColorScales[colorOptions.scale] ?? ColorScales.turbo;
  const factor = (colorScale.length - 1) / (max - min);
  const valueInRange = Math.min(max, Math.max(min, value));
  const idx = Math.floor((valueInRange - min) * factor);
  return colorScale[colorOptions.invertScale ? colorScale.length - idx - 1 : idx] ?? [0, 0, 0];
}

// render heatmap data to the bitmap image, 1 pixel per data point
export function renderDataToBitmap(
  data: HeatMapChartData,
  dataBitmap: ImageData,
  colorOptions: HeatmapCanvasColorOptions,
  cols: number,
  rows: number,
  startMs: number
) {
  const stepMs =
    typeof data.timeRange.stepMs === 'number' && data.timeRange.stepMs > 0
      ? data.timeRange.stepMs
      : MIN_STEP_FALLBACK_MS;
  for (let entry = 0; entry < data.timeSlices.length; entry++) {
    const histogram = data.timeSlices[entry];
    if (histogram) {
      const col = Math.floor((histogram.timestamp - startMs) / stepMs);
      for (let dpIdx = 0; dpIdx < histogram.dataPoints.length; dpIdx++) {
        const dataPoint = histogram.dataPoints[dpIdx];
        if (dataPoint) {
          if (dataPoint.value < 0) continue; // treat negative values as missing data
          if (colorOptions.hideZeros && dataPoint.value === 0) continue;
          for (let i = 0; i < dataPoint.bucketSpan; i++) {
            const row = dataPoint.bucketIndex + i;
            // 4 entries per pixel in the bitmap array (rgba)
            const idx = 4 * ((rows - row - 1) * cols + col);
            // find the color for the datapoint value PER min bucket (so we divide by bucket span)
            writePixel(
              dataBitmap.data,
              idx,
              colorLookup(dataPoint.value / dataPoint.bucketSpan, data.maxValue, colorOptions)
            );
          }
        }
      }
    }
  }
}

/**
 * Renders the heatmap to the canvas.
 *
 * Strategy:
 * - create a bitmap image with 1 pixel corresponding to each datapoint
 * - render the bitmap into an offscreen canvas
 * - copy the offscreen canvas into the onscreen canvas, scaling to the expected size
 *
 * The offscreen canvas is needed because putImageData() does not support scaling
 * and canvas transformations do not apply to bitmap images.
 *
 * By default, we paint the entire area the 0-value color before drawing pixels for datapoint
 * values. In this way missing, 0-value, and negative-value datapoints are all rendered the same.
 *
 * If the "Hide Zero" option is selected, we leave blank all missing, 0-value, and negative-value
 * datapoints.
 */
function draw(
  canvas: HTMLCanvasElement,
  data: HeatMapChartData,
  width: number,
  height: number,
  colorOptions: HeatmapCanvasColorOptions
) {
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    if (colorOptions.hideZeros) {
      // clear the canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    } else {
      // paint the canvas in the 0-value color
      const zeroColor = colorLookup(0, data.maxValue, colorOptions);
      ctx.fillStyle = `rgb(${zeroColor[0]},${zeroColor[1]},${zeroColor[2]})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    // disable anti-aliasing, to ensure crisp cell borders when scaling the bitmap
    ctx.imageSmoothingEnabled = false;

    const { startMs, endMs } = data.timeRange;
    const stepMs =
      typeof data.timeRange.stepMs === 'number' && data.timeRange.stepMs > 0
        ? data.timeRange.stepMs
        : MIN_STEP_FALLBACK_MS;
    const cols = 1 + (endMs - startMs) / stepMs;
    const rows = data.buckets.length;

    // build a bitmap image from the heatmap data, and render the bitmap to the offscreen canvas
    const offscreenCanvas = new OffscreenCanvas(cols, rows);
    const offscreenContext = offscreenCanvas.getContext('2d');
    if (offscreenContext && rows > 0 && cols > 0) {
      const dataBitmap = offscreenContext.createImageData(cols, rows);
      renderDataToBitmap(data, dataBitmap, colorOptions, cols, rows, startMs);
      offscreenContext.putImageData(dataBitmap, 0, 0);
    }

    // render the offscreen canvas to the onscreen canvas, scaling to correct size
    if (offscreenCanvas.width > 0 && offscreenCanvas.height > 0) {
      ctx.drawImage(
        offscreenCanvas,
        0,
        0,
        offscreenCanvas.width,
        offscreenCanvas.height,
        0,
        0,
        canvas.width,
        canvas.height
      );
    }

    // show gridlines if col/row density is not too large
    if (canvas.width / cols > 12 && canvas.height / rows > 5) {
      ctx.strokeStyle = 'rgba(225,225,225, 0.7)';
      ctx.lineWidth = 1;

      // columns
      ctx.beginPath();
      for (let col = 0; col < cols; col++) {
        const x = 0.5 + Math.round((col * canvas.width) / cols);
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
      }
      ctx.stroke();

      // rows
      ctx.beginPath();
      for (let row = 0; row < rows; row++) {
        const y = 0.5 + Math.round((row * canvas.height) / rows);
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
      }
      ctx.stroke();
    }
  }
}

export interface HeatMapCanvasProps {
  /**
   * Width of the canvas
   */
  width: number;
  /**
   * Height of the canvas
   */
  height: number;
  /**
   * heatmap chart data
   */
  data: HeatMapChartData;
  /**
   * color-related configuration options
   */
  colorOptions: HeatmapCanvasColorOptions;
}

export function HeatMapCanvas({ width, height, data, colorOptions }: HeatMapCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) draw(canvas, data, width, height, colorOptions);
  }, [width, height, data, colorOptions]);

  return <canvas ref={canvasRef} />;
}
